"use client";

/**
 * Marketing header nav — hover mega-menus for Platform / Solutions /
 * Resources, plain links for Customers / Pricing. One shared Radix
 * viewport morphs between panels (the modern-SaaS glide), items are
 * icon-tile + title + one-line benefit. Marketing voice, not docs.
 */

import type { LucideIcon } from "lucide-react";
import {
  Activity,
  BookOpen,
  Box,
  Briefcase,
  Building2,
  GitFork,
  History,
  Layers,
  MessageCircle,
  Newspaper,
  Rocket,
  SquareTerminal,
  Users,
  Webhook,
  Workflow,
  Wrench,
} from "lucide-react";
import Link from "next/link";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/shared/components/ui/navigation-menu";

interface MenuEntry {
  title: string;
  description: string;
  href: string;
  icon: LucideIcon;
}

const PLATFORM: MenuEntry[] = [
  {
    title: "Agent Sessions",
    description: "Assign work, watch it ship.",
    href: "/platform/sessions",
    icon: Workflow,
  },
  {
    title: "Live IDE",
    description: "See and steer every change, live.",
    href: "/platform/ide",
    icon: SquareTerminal,
  },
  {
    title: "Sandboxes",
    description: "Every task runs in its own safe environment.",
    href: "/platform/sandboxes",
    icon: Box,
  },
  {
    title: "Forks & Workspaces",
    description: "Try bold changes without touching prod.",
    href: "/platform/workspaces",
    icon: GitFork,
  },
  {
    title: "Observability",
    description: "Know exactly what your agents did, and why.",
    href: "/platform/observability",
    icon: Activity,
  },
  {
    title: "API & Webhooks",
    description: "Plug TailStrike into the rest of your stack.",
    href: "/platform/api",
    icon: Webhook,
  },
];

const SOLUTIONS: MenuEntry[] = [
  {
    title: "For startups",
    description: "Ship like a team ten times your size.",
    href: "/solutions/startups",
    icon: Rocket,
  },
  {
    title: "For agencies",
    description: "Deliver more client work in parallel.",
    href: "/solutions/agencies",
    icon: Briefcase,
  },
  {
    title: "For enterprises",
    description: "SSO, audit, and governance built in.",
    href: "/solutions/enterprise",
    icon: Building2,
  },
  {
    title: "Parallel shipping",
    description: "Run many features at once, safely.",
    href: "/solutions/parallel-shipping",
    icon: Layers,
  },
  {
    title: "Modernization",
    description: "Give legacy codebases new momentum.",
    href: "/solutions/modernization",
    icon: Wrench,
  },
];

const RESOURCES: MenuEntry[] = [
  { title: "Docs", description: "Get the most out of TailStrike.", href: "/docs", icon: BookOpen },
  {
    title: "Blog",
    description: "Product news and engineering notes.",
    href: "/blog",
    icon: Newspaper,
  },
  {
    title: "Changelog",
    description: "What shipped, week by week.",
    href: "/changelog",
    icon: History,
  },
  {
    title: "Customers",
    description: "Teams building with TailStrike.",
    href: "/customers",
    icon: Users,
  },
  { title: "Contact", description: "Talk to a human.", href: "/contact", icon: MessageCircle },
];

function MenuTile({ entry }: { entry: MenuEntry }) {
  const Icon = entry.icon;
  return (
    <NavigationMenuLink asChild>
      <Link
        href={entry.href}
        className="group/tile flex items-start gap-3 rounded-xl p-3 transition-colors hover:bg-[color-mix(in_oklch,var(--color-primary)_6%,transparent)]"
      >
        <span className="mt-0.5 grid size-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
          <Icon className="size-[18px]" strokeWidth={1.75} />
        </span>
        <span>
          <span className="block text-[13.5px] font-medium text-foreground">{entry.title}</span>
          <span className="mt-0.5 block text-[12.5px] leading-snug text-muted-foreground">
            {entry.description}
          </span>
        </span>
      </Link>
    </NavigationMenuLink>
  );
}

function PlainNavLink({ href, label }: { href: string; label: string }) {
  return (
    <NavigationMenuItem>
      <NavigationMenuLink asChild>
        <Link
          href={href}
          className="inline-flex rounded-full px-3 py-2 text-sm text-foreground/80 transition-colors duration-150 hover:bg-[color-mix(in_oklch,var(--color-primary)_7%,transparent)] hover:text-foreground"
        >
          {label}
        </Link>
      </NavigationMenuLink>
    </NavigationMenuItem>
  );
}

export function MarketingNav() {
  return (
    <NavigationMenu className="hidden md:flex" delayDuration={75}>
      <NavigationMenuList>
        <NavigationMenuItem>
          <NavigationMenuTrigger>Platform</NavigationMenuTrigger>
          <NavigationMenuContent>
            <div className="grid w-[560px] grid-cols-2 gap-1 p-2">
              {PLATFORM.map((e) => (
                <MenuTile key={e.href} entry={e} />
              ))}
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>

        <NavigationMenuItem>
          <NavigationMenuTrigger>Solutions</NavigationMenuTrigger>
          <NavigationMenuContent>
            <div className="grid w-[320px] gap-1 p-2">
              {SOLUTIONS.map((e) => (
                <MenuTile key={e.href} entry={e} />
              ))}
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>

        <PlainNavLink href="/customers" label="Customers" />

        <NavigationMenuItem>
          <NavigationMenuTrigger>Resources</NavigationMenuTrigger>
          <NavigationMenuContent>
            <div className="grid w-[300px] gap-1 p-2">
              {RESOURCES.map((e) => (
                <MenuTile key={e.href} entry={e} />
              ))}
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>

        <PlainNavLink href="/pricing" label="Pricing" />
      </NavigationMenuList>
    </NavigationMenu>
  );
}
